import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMemo, useState } from "react";
import { AdminCard, SectionHeader, StatusPill, EmptyState, btnGhost } from "@/lib/admin/ui";
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor, useSensor, useSensors,
  closestCorners, type DragEndEvent, type DragStartEvent,
} from "@dnd-kit/core";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import { Kanban, Clock, User, Zap, AlertTriangle, ListChecks } from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/projects")({
  component: ProjectsKanban,
});

const COLUMNS: { id: string; label: string; accent: string }[] = [
  { id: "new_order", label: "New Order", accent: "from-violet-500/20" },
  { id: "waiting_for_material", label: "Waiting Material", accent: "from-amber-500/20" },
  { id: "wireframe", label: "Wireframe", accent: "from-blue-500/20" },
  { id: "design", label: "Design", accent: "from-pink-500/20" },
  { id: "development", label: "Development", accent: "from-fuchsia-500/20" },
  { id: "internal_review", label: "Internal Review", accent: "from-cyan-500/20" },
  { id: "client_review", label: "Client Review", accent: "from-teal-500/20" },
  { id: "seo", label: "SEO", accent: "from-lime-500/20" },
  { id: "ready", label: "Ready", accent: "from-emerald-500/20" },
  { id: "delivered", label: "Delivered", accent: "from-emerald-600/20" },
];

type Project = {
  id: string; name: string; kanban_status: string; plan: string;
  priority: string; progress: number; delivery_date: string | null;
  revisions_left: number | null; estimated_hours: number | null; time_spent: number | null;
  health_score: number | null; project_manager: string | null;
};

function ProjectsKanban() {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }));

  const { data: projects } = useQuery({
    queryKey: ["admin-projects-kanban"],
    queryFn: async () => {
      const { data } = await supabase.from("projects").select("id, name, kanban_status, plan, priority, progress, delivery_date, revisions_left, estimated_hours, time_spent, health_score, project_manager").order("created_at", { ascending: false });
      return (data ?? []) as Project[];
    },
    refetchInterval: 30_000,
  });

  const grouped = useMemo(() => {
    const g: Record<string, Project[]> = {};
    for (const col of COLUMNS) g[col.id] = [];
    for (const p of projects ?? []) {
      const col = p.kanban_status && g[p.kanban_status] ? p.kanban_status : "new_order";
      g[col].push(p);
    }
    return g;
  }, [projects]);

  const activeProject = (projects ?? []).find((p) => p.id === activeId);

  const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const onDragEnd = async (e: DragEndEvent) => {
    setActiveId(null);
    const projectId = String(e.active.id);
    const newCol = e.over?.id ? String(e.over.id) : null;
    if (!newCol) return;
    const proj = (projects ?? []).find((p) => p.id === projectId);
    if (!proj || proj.kanban_status === newCol) return;

    // Optimistic
    qc.setQueryData(["admin-projects-kanban"], (old: Project[] | undefined) =>
      (old ?? []).map((p) => p.id === projectId ? { ...p, kanban_status: newCol } : p));

    const { error } = await supabase.from("projects").update({ kanban_status: newCol }).eq("id", projectId);
    if (error) {
      toast.error(error.message);
      qc.invalidateQueries({ queryKey: ["admin-projects-kanban"] });
    } else {
      toast.success(`${proj.name} → ${COLUMNS.find((c) => c.id === newCol)?.label}`);
      await supabase.from("activity_log").insert({
        entity_type: "project", entity_id: projectId, action: "moved",
        meta: { from: proj.kanban_status, to: newCol },
      });
    }
  };

  return (
    <div className="max-w-full space-y-6">
      <SectionHeader eyebrow="Delivery" title="Projects Kanban" description={`${(projects ?? []).length} progetti · trascina per aggiornare lo stato`}
        actions={<button className={btnGhost}><ListChecks className="h-3.5 w-3.5" /> Vista Lista</button>} />

      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4 -mx-4 px-4 snap-x">
          {COLUMNS.map((col) => (
            <KanbanColumn key={col.id} col={col} projects={grouped[col.id] ?? []} />
          ))}
        </div>
        <DragOverlay>
          {activeProject && <ProjectCard project={activeProject} dragging />}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

function KanbanColumn({ col, projects }: { col: { id: string; label: string; accent: string }; projects: Project[] }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.id });
  return (
    <div ref={setNodeRef}
      className={`w-72 shrink-0 snap-start rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl flex flex-col transition ${isOver ? "border-fuchsia-400/40 bg-fuchsia-500/[0.03]" : ""}`}>
      <div className={`px-3 py-2.5 rounded-t-2xl bg-gradient-to-b ${col.accent} to-transparent border-b border-white/[0.06]`}>
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold">{col.label}</div>
          <span className="text-[10px] text-white/50 tabular-nums bg-white/5 px-1.5 py-0.5 rounded-full">{projects.length}</span>
        </div>
      </div>
      <div className="p-2 space-y-2 min-h-[200px] max-h-[calc(100vh-14rem)] overflow-y-auto">
        {projects.length === 0 ? (
          <div className="py-8 text-center text-[11px] text-white/25">Trascina qui</div>
        ) : projects.map((p) => <ProjectCard key={p.id} project={p} />)}
      </div>
    </div>
  );
}

function ProjectCard({ project, dragging }: { project: Project; dragging?: boolean }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: project.id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
  const days = project.delivery_date ? differenceInDays(new Date(project.delivery_date), new Date()) : null;
  const health = project.health_score ?? 100;
  const priorityColor = project.priority === "urgent" ? "text-red-400" : project.priority === "high" ? "text-orange-400" : project.priority === "low" ? "text-white/40" : "text-cyan-300";

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}
      className={`group rounded-xl border border-white/[0.08] bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-3 cursor-grab active:cursor-grabbing hover:border-white/20 transition ${isDragging || dragging ? "opacity-40" : ""}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="font-medium text-sm truncate">{project.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-white/40 mt-0.5">{project.plan}</div>
        </div>
        {project.priority === "urgent" && <AlertTriangle className="h-3.5 w-3.5 text-red-400 shrink-0" />}
      </div>

      <div className="h-1 rounded-full bg-white/5 overflow-hidden mb-2">
        <div className="h-full bg-gradient-to-r from-fuchsia-500 to-cyan-400" style={{ width: `${project.progress ?? 0}%` }} />
      </div>

      <div className="flex items-center flex-wrap gap-1.5 text-[10px]">
        <span className={`inline-flex items-center gap-0.5 ${priorityColor}`}>
          <Zap className="h-2.5 w-2.5" />{project.priority}
        </span>
        {days !== null && (
          <span className={`inline-flex items-center gap-0.5 ${days < 0 ? "text-red-400" : days <= 3 ? "text-amber-300" : "text-white/50"}`}>
            <Clock className="h-2.5 w-2.5" />{days < 0 ? `${Math.abs(days)}g fa` : `${days}g`}
          </span>
        )}
        {project.project_manager && (
          <span className="inline-flex items-center gap-0.5 text-white/50">
            <User className="h-2.5 w-2.5" />{project.project_manager.split(" ")[0]}
          </span>
        )}
      </div>

      <div className="mt-2 flex items-center justify-between text-[10px] text-white/40">
        <span>Health</span>
        <div className="flex items-center gap-1">
          <div className="h-1 w-12 rounded-full bg-white/5 overflow-hidden">
            <div className={`h-full ${health >= 80 ? "bg-emerald-400" : health >= 50 ? "bg-amber-400" : "bg-red-400"}`} style={{ width: `${health}%` }} />
          </div>
          <span className="tabular-nums">{health}</span>
        </div>
      </div>
    </div>
  );
}
